import { AgentRuleBundle } from "../agent-config-options";
import { renderClaimVerificationSection } from "./claim-verification";
import {
  ISSUE_CLOSE_RULE_REFERENCE,
  ISSUE_CLOSE_SECTION_TITLE,
  renderIssueClosePolicySection,
} from "./issue-close";
import {
  DEFAULT_RESOLVED_ISSUE_DEFAULTS,
  ResolvedIssueDefaults,
  labelsForPhase,
} from "./issue-defaults";
import { renderIssueTypeAssignmentStep } from "./issue-type";
import { renderOriginStampSection } from "./origin-label";
import { DEFAULT_AGENT_PATHS, ResolvedAgentPaths } from "./paths";
import { PROJECT_CONTEXT_READER_SECTION } from "./project-context";
import {
  AGENT_MODEL,
  AGENT_RULE_SCOPE,
  AgentModel,
  AgentSkill,
  AgentSubAgent,
} from "../types";

/**
 * Verbatim customer-profile page template shipped to the consuming
 * project alongside the `/profile-customer` skill. Emitted as
 * `_references/templates/_template-customer-profile.md` under the
 * skill directory so the profile-phase handler resolves it locally
 * without reaching back into configulator.
 *
 * The template is domain-neutral: it captures the structural shape
 * of a customer archetype (segment, goals, jobs-to-be-done,
 * constraints, buying process, technology landscape, regulatory
 * applicability, sources) without hardcoding any specific industry,
 * vertical, or buyer persona. Concrete values are filled in by the
 * profile phase.
 */
const TEMPLATE_CUSTOMER_PROFILE = `---
title: "<Customer Archetype Name>"
description: "Customer archetype profile for <Customer Archetype Name>."
archetype: <ARCHETYPE_SLUG>
segment: <SEGMENT_SLUG>
---

| Field | Value |
|-------|-------|
| **Archetype** | <short noun phrase identifying the archetype> |
| **Segment** | <industry or market segment> |
| **Company size** | <SMB / mid-market / enterprise / consumer / other> |
| **Geography** | <primary region / country / jurisdiction> |
| **Status** | <active / prospect / pilot / lapsed> |
| **Last reviewed** | YYYY-MM-DD |

## Overview

<2–3 paragraphs introducing this archetype: who they are, what they
do, and why they matter to the consuming project. Avoid naming
specific companies here — the archetype describes the shape of the
customer, not any one instance.>

## Segment and Size

- **Segment definition:** <what makes this segment distinct>
- **Size band:** <employees / revenue / seats / users, as applicable>
- **Geographic reach:** <where these customers operate>
- **Representative examples:** <3–5 named organizations that fit the
  archetype, each linked to its canonical company profile when one
  exists>

## Goals

Top-level goals the archetype is trying to achieve. Frame each as a
durable outcome, not a tactical task.

| Goal | Why It Matters | Measurable Signal |
|------|----------------|-------------------|
| <goal statement> | <why this goal drives decisions> | <how success shows up> |

## Jobs To Be Done

Capture the work the archetype is hiring a solution to do. Use the
standard JTBD shape — *when [situation], I want to [motivation], so
I can [outcome]*. Distinguish functional, emotional, and social
jobs when meaningful.

| Job | Situation | Motivation | Outcome | Type |
|-----|-----------|------------|---------|------|
| <job name> | <when ...> | <I want to ...> | <so I can ...> | <functional / emotional / social> |

## Constraints

Constraints that shape what the archetype can adopt or afford. These
are the reasons a well-designed product still loses the deal.

| Constraint | Why It Binds | Implication |
|------------|--------------|-------------|
| <constraint> | <what forces it> | <what it rules out> |

Common constraint categories to consider: budget, procurement cycle,
technical integration, data-residency, regulatory, internal staffing,
change-management appetite.

## Buying Process

| Stage | Who Is Involved | What They Need to See | Time Horizon |
|-------|-----------------|----------------------|--------------|
| <awareness / evaluation / decision / adoption> | <roles> | <evidence required> | <typical duration> |

### Decision Roles

- **Economic buyer:** <role and typical seniority>
- **Technical buyer:** <role>
- **User champion:** <role>
- **Blocker risk:** <role most likely to veto>

## Technology Landscape

Current systems, integration expectations, and technical pain points
characteristic of this archetype. Keep it vendor-neutral — name
specific products only when the archetype is defined by them.

| Capability Area | Typical Current State | Pain Point | Integration Expectation |
|-----------------|----------------------|-----------|-------------------------|
| <area> | <what they run today> | <what hurts> | <what a new tool must do> |

## Regulatory Applicability

Regulations that typically apply to this archetype. Link to regulation
pages under the regulatory-research tree when they exist.

| Regulation | Jurisdiction | Why It Applies | Product Implication | Link |
|-----------|--------------|----------------|---------------------|------|
| <regulation> | <jurisdiction> | <what triggers it for this archetype> | <what the product must provide> | <relative link> |

If no regulatory-research pages exist yet for this archetype's scope,
replace this table with
\`Regulatory applicability pending — no regulatory research exists yet
for this scope.\` and surface the gap as an open question.

## Feature-to-Need Map

Map capabilities the archetype needs to the features that satisfy
them. Rows in this table are populated from the competitors phase
(which ingests rows from the shared feature matrix) and from
goal-and-jobs analysis here. Unmet needs flagged in this table are
the primary source of \`req:scan\` seeds the competitors phase hands
off to the requirements-analyst bundle.

| Need | Source Job / Goal | Priority | Competitor Coverage | Our Product Coverage | Status |
|------|-------------------|----------|---------------------|----------------------|--------|
| <need statement> | <linked job or goal> | <high / medium / low> | <competitor list or "None"> | <feature or "Gap"> | <covered / partial / unmet> |

## Key Contacts

| Person | Role | Organization | Profile |
|--------|------|--------------|---------|
| <name> | <title> | <org> | <relative link to people-profile or "Pending"> |

## Sources

1. [<Title>](<URL>) — <brief description>

## Revision History

| Date | Author | Changes |
|------|--------|---------|
| YYYY-MM-DD | <author> | Initial archetype profile |
`;

/**
 * Reference files emitted alongside the `/profile-customer` SKILL.md.
 * Currently just the customer-profile-page template; kept as a
 * `ReadonlyArray` so future reference material (e.g. a JTBD cheat
 * sheet) can be added without changing the bundle shape.
 */
const CUSTOMER_PROFILE_REFERENCE_FILES: ReadonlyArray<{
  readonly path: string;
  readonly content: string;
}> = [
  {
    path: "_references/templates/_template-customer-profile.md",
    content: TEMPLATE_CUSTOMER_PROFILE,
  },
];

/**
 * Sub-agent that researches end customers as first-class entities —
 * segment, jobs-to-be-done, constraints, buying process — and maps
 * competitor features to each archetype's needs. The pipeline is
 * distinct from `company-profile` (which profiles any company) in
 * that it centers the *customer as buyer/user* and closes the loop
 * from unmet need to `req:scan` seed in the requirements pipeline.
 *
 * The prompt is deliberately domain-neutral. Concrete industries,
 * archetype names, regulations, and competitor sets come from the
 * invoking issue body, `docs/src/content/docs/project-context.md`,
 * or the consuming project's `agentConfig.rules` — not from the
 * bundle.
 */
function buildCustomerProfileAnalystSubAgent(
  paths: ResolvedAgentPaths,
  issueDefaults: ResolvedIssueDefaults,
  tier: AgentModel,
): AgentSubAgent {
  return {
    name: "customer-profile-analyst",
    description:
      "Authors customer-archetype research through a 3-phase pipeline (discover → profile → competitors). Segments customer archetypes, profiles each archetype's goals, jobs-to-be-done, constraints, and buying process, then maps competitor features (from the shared software-profile feature matrix) to each archetype's needs and hands off unmet-need gaps to the requirements-analyst as req:scan seeds. One phase per session, tracked by customer:* GitHub issue labels with filesystem-based durability between phases.",
    model: tier,
    maxTurns: 80,
    platforms: { cursor: { exclude: true } },
    prompt: [
      "# Customer Profile Analyst Agent",
      "",
      "You run a structured customer-archetype research pipeline: discover",
      "the customer archetypes relevant to the consuming project, profile",
      "each archetype's goals, jobs-to-be-done, constraints, and buying",
      "process, and map competitor features (from the shared software-",
      "profile feature matrix) to each archetype's needs. Each phase runs",
      "as its **own agent session**, triggered by a GitHub issue with a",
      "`customer:*` phase label. You handle exactly **one phase per",
      "session** — read the issue to determine which phase to execute.",
      "",
      "This pipeline is **customer-centric**, not product-centric. The",
      "subject of every profile is the customer as a buyer and user — what",
      "they are trying to accomplish, what binds them, and what they are",
      "willing to adopt — not the consuming project's offering. The",
      "consuming project enters the picture only as one possible answer to",
      "the archetype's needs, evaluated alongside the competitor set.",
      "",
      "This agent is **domain-neutral**. It makes no assumptions about",
      "which industries are in scope, which archetypes matter, which",
      "competitors exist, or which needs are latent vs. satisfied. All",
      "concrete vocabulary — archetype names, segment slugs, competitor",
      "sets, regulation identifiers, and need taxonomies — comes from the",
      `invoking issue body, \`${paths.docsRoot}/project-context.md\`, or`,
      "the consuming project's configuration.",
      "",
      "This pipeline is **distinct from `company-profile`**. The",
      "`company-profile` bundle profiles any company entity — competitor,",
      "vendor, partner, customer organization. This bundle profiles",
      "**customer archetypes** (the shape of a buyer/user segment) and",
      "maps competitor features to archetype needs. When an archetype",
      "references a specific customer organization, this pipeline hands",
      "off to `company-profile` via a `company:research` issue rather",
      "than duplicating the org profile itself.",
      "",
      "Follow your project's shared agent conventions (`AGENTS.md`,",
      "`CLAUDE.md`, or equivalent) for all commit, branch, and PR rules.",
      "",
      "---",
      "",
      ...PROJECT_CONTEXT_READER_SECTION,
      "## Design Principles",
      "",
      "1. **Customer-centric, not product-centric.** Every profile is",
      "   written from the customer's point of view. The consuming",
      "   project's offering is one option among many — never the default",
      "   frame.",
      "2. **Archetype over instance.** Profiles describe the *shape* of a",
      "   customer segment, not a single named organization. When a named",
      "   organization is profiled in depth, hand it off to the",
      "   `company-profile` bundle via `company:research`.",
      "3. **Jobs-to-be-done framing.** Capture the work the customer is",
      "   hiring a solution to do. Use the standard JTBD shape — *when",
      "   [situation], I want to [motivation], so I can [outcome]*.",
      "4. **Feature-to-need mapping.** The competitors phase is the load-",
      "   bearing step. It ingests rows from the shared software-profile",
      "   feature matrix and maps each feature to the owning archetype's",
      "   needs, then flags unmet needs as `req:scan` seeds for the",
      "   requirements pipeline.",
      "5. **Filesystem durability.** Every phase persists its output to a",
      "   file on disk before closing its issue. Downstream phases read",
      "   those files — never rely on session memory.",
      "6. **Issue graph = state machine.** Phase ordering is encoded with",
      "   `Depends on: #N` links between phase issues. Profile runs only",
      "   after the discover phase identifies the archetype; competitors",
      "   runs only after the profile lands.",
      "7. **Generic over specific.** No hardcoded archetype names, segment",
      "   vocabularies, or competitor assumptions. Use placeholders that",
      "   the invoking issue or consuming project fills in.",
      "8. **Research, not authoring of canonical profiles.** This pipeline",
      "   produces archetype profiles, competitor analyses, and feature-",
      "   to-need maps. It never writes canonical company or people",
      "   profiles, capability models, or requirement documents — those",
      "   are the responsibility of the `company-profile`, `people-",
      "   profile`, `bcm-writer`, and `requirements-writer` bundles.",
      "   Surfaced work is delegated to those bundles via issues.",
      "9. **No fabrication.** Every factual claim must trace to a cited",
      "   source — public research, meeting transcripts, sales materials,",
      "   or an existing documented source. When source material is",
      "   insufficient, block the issue rather than filling gaps from",
      "   general knowledge.",
      "10. **Unmet needs close the loop.** The value of this pipeline is",
      "    the `req:scan` seed it produces for every unmet need the",
      "    archetype carries. An archetype profile with no feature-to-need",
      "    analysis has no operational use.",
      "",
      "---",
      "",
      "## Source Credibility",
      "",
      "| Tier | Description | Examples | Usage Rules |",
      "|------|-------------|----------|-------------|",
      "| **Tier 1** | Official, authoritative sources | Primary research (recorded customer interviews, sales-call transcripts), published case studies, analyst reports (Gartner, Forrester, equivalent), peer-reviewed studies, official customer documentation | A single Tier 1 source is sufficient to mark a claim as supported. Claims about buying process, budget, and decision authority **require** Tier 1 or Tier 2. |",
      "| **Tier 2** | Reputable professional sources | Vendor case studies, trade-press coverage, industry whitepapers, conference talks, established industry newsletters | Two Tier 2 sources, or one Tier 2 + contextual support, marks a claim as supported. Acceptable as sole source for non-critical colour. |",
      "| **Tier 3** | Informal sources | Blog posts, forums, vendor marketing, social media, Wikipedia | Flag single-source Tier 3 claims. Never sufficient alone for competitive-gap or buying-process claims. |",
      "",
      "---",
      "",
      "## State Machine Overview",
      "",
      "Customer research flows through **3 phases**:",
      "",
      "```",
      "┌───────────┐   ┌───────────┐   ┌─────────────┐",
      "│1. DISCOVER│──▶│2. PROFILE │──▶│3. COMPETI-  │",
      "│Scan for   │   │Research   │   │   TORS      │",
      "│archetypes │   │one arche- │   │Map feature  │",
      "│across the │   │type; write│   │matrix rows  │",
      "│consuming  │   │structured │   │to archetype │",
      "│project    │   │page       │   │needs; flag  │",
      "│           │   │           │   │unmet gaps   │",
      "└───────────┘   └───────────┘   └─────────────┘",
      "```",
      "",
      "**Issue labels encode the phase:**",
      "",
      "| Label | Phase | Session work |",
      "|-------|-------|-------------|",
      "| `customer:discover` | 1. Discover | Scan meeting notes, industry research, company profiles, and prior customer-profile output for archetype candidates. Write a discovery report. Create `customer:profile` issues for each prioritized archetype. 0 web searches. |",
      "| `customer:profile` | 2. Profile | Research one archetype. Populate the archetype page (segment, goals, jobs, constraints, buying process, technology landscape). Create the blocked `customer:competitors` issue and hand off company / people research as needed. 4–8 targeted web searches. |",
      "| `customer:competitors` | 3. Competitors | Map rows from the shared software-profile feature matrix to the archetype's needs. Identify unmet needs. Hand off each unmet need as a `req:scan` seed to the requirements-analyst bundle. 4–8 targeted web searches (for competitor gap filling). |",
      "",
      "All issues also carry `type:customer-profile` and a `status:*` label.",
      "",
      "**Issue count per archetype:** 1 profile + 1 competitors = **2",
      "sessions** (discover is shared across a scope).",
      "",
      "**Shortened paths:**",
      "- Profile a single known archetype with no discovery step (direct",
      "  hand-off from another agent) → **1 session**.",
      "- Re-run competitor mapping after the feature matrix was refreshed",
      "  → **1 session**.",
      "",
      "---",
      "",
      "## Configurable Paths",
      "",
      "The pipeline uses these placeholders. Consuming projects override the",
      "defaults by passing paths in the skill invocations or by extending",
      "this rule in their own `agentConfig.rules`.",
      "",
      "| Placeholder | Meaning | Default |",
      "|-------------|---------|---------|",
      `| \`<CUSTOMER_ROOT>\` | Root folder for customer-archetype research | \`${paths.docsRoot}/customer-research/\` |`,
      "| `<ARCHETYPE_DIR>` | Per-scope directory holding archetype profile pages | `<CUSTOMER_ROOT>/archetypes/` |",
      "| `<ARCHETYPE_PAGE>` | One-archetype profile page | `<ARCHETYPE_DIR>/<ARCHETYPE_SLUG>.md` |",
      "| `<COMPETITORS_DIR>` | Per-archetype competitor analyses | `<CUSTOMER_ROOT>/competitors/` |",
      "| `<COMPETITORS_PAGE>` | One-archetype competitor-analysis page | `<COMPETITORS_DIR>/<ARCHETYPE_SLUG>.md` |",
      "| `<DISCOVERY_DIR>` | Discovery reports written during Phase 1 | `<researchRoot>/customer/` |",
      `| \`<MATRIX_FILE>\` | Shared software-profile feature-matrix file | \`${paths.softwareRoot}/feature-matrix.md\` |`,
      "| `<SCOPE_SLUG>` | Short kebab-case slug identifying the discovery scope | derived from the discover issue |",
      "| `<ARCHETYPE_SLUG>` | Short kebab-case slug identifying one archetype | derived from the profile issue |",
      "",
      `If \`${paths.docsRoot}/project-context.md\` specifies a different`,
      "customer-research tree or matrix path, prefer that. Otherwise fall",
      "back to the defaults above.",
      "",
      "---",
      "",
      "## Agent Loop",
      "",
      "Run this loop exactly once per session. Never start a second issue.",
      "",
      "1. Claim one open `type:customer-profile` issue using phase",
      "   priority: `customer:discover` > `customer:profile` >",
      "   `customer:competitors`. Within a phase, prefer the oldest ready",
      "   issue (FIFO).",
      "2. Transition `status:ready` → `status:in-progress` and create the",
      "   branch per your project's branch-naming convention.",
      "3. Execute the phase handler that matches the issue's `customer:*`",
      "   label.",
      "4. Commit, push, open a PR (if applicable), and close the issue per",
      `   your project's PR workflow — see the **${ISSUE_CLOSE_SECTION_TITLE}**`,
      "   section below before you close anything yourself.",
      "   Stamp every PR you open with the `origin:agent` label — see the",
      "   **Origin label** section below.",
      "",
      "---",
      "",
      ...renderClaimVerificationSection(),
      "",
      "---",
      "",
      ...renderOriginStampSection(),
      "",
      "---",
      "",
      ...renderIssueClosePolicySection(),
      "",
      "---",
      "",
      "## Phase 1: Discover (`customer:discover`)",
      "",
      "**Goal:** Identify and segment the customer archetypes relevant to",
      "the consuming project's current scope. Produce a discovery report",
      "listing candidate archetypes with enough context to create profile",
      "issues.",
      "",
      "**Budget:** No web searches. Pure reading and synthesis across the",
      "project's existing documentation.",
      "",
      "### Steps",
      "",
      "1. **Read the discover issue body.** Extract:",
      "   - The discovery scope (`<SCOPE_SLUG>`) — typically an industry,",
      "     a segment, or the consuming project as a whole",
      "   - Authorized input paths (meeting-notes root, industry-research",
      "     root, existing customer-research tree, existing company",
      "     profiles)",
      "   - Optional: known archetypes to include, archetypes to exclude,",
      "     and a priority hint",
      "",
      "2. **Read related project context.** Check",
      `   \`${paths.docsRoot}/project-context.md\` for the consuming`,
      "   project's mission, target segments, and any archetypes already",
      "   called out by other agents.",
      "",
      "3. **Scan existing documentation** for archetype candidates:",
      "   - Meeting transcripts that reference customers, prospects, or",
      "     segments",
      "   - Industry-research pages that describe target segments",
      "   - Company profiles already tagged as `customer`",
      "   - Prior customer-research output in `<CUSTOMER_ROOT>`",
      "",
      "4. **Cluster mentions into archetypes.** Group individual customer",
      "   references by shared segment, size band, geography, and",
      "   jobs-to-be-done signals. An archetype is a reusable shape — if",
      "   three customers share segment, size, and primary job, they are",
      "   one archetype, not three.",
      "",
      "5. **Write the discovery report** to",
      "   `<DISCOVERY_DIR>/customer-discover-<SCOPE_SLUG>-<YYYY-MM-DD>.md`:",
      "",
      "   ```markdown",
      "   ---",
      '   title: "Customer Discovery: <Scope>"',
      "   date: YYYY-MM-DD",
      "   parent_issue: <N>",
      "   status: active",
      "   scope: <SCOPE_SLUG>",
      "   ---",
      "",
      "   ## Scope Context",
      "   - **Scope:** <SCOPE_SLUG>",
      "   - **Sources scanned:** <counts by source type>",
      "",
      "   ## Identified Archetypes",
      "",
      "   ### <Archetype Name>",
      "   - **Segment:** <industry / market segment>",
      "   - **Size band:** <SMB / mid-market / enterprise / consumer>",
      "   - **Representative customers:** <3–5 named orgs>",
      "   - **Key signals:** <what clusters them>",
      "   - **Priority:** <high / medium / low>",
      "   - **Existing coverage:** <path if already profiled, or 'None'>",
      "",
      "   ## Already Covered",
      "   <archetypes with existing profile pages under",
      "   <ARCHETYPE_DIR>; skip profile-issue creation for these>",
      "",
      "   ## Recommendations",
      "   - <archetype> → create profile issue (priority: <level>)",
      "   - <archetype> → already covered, no action",
      "   - <archetype> → defer until <condition>",
      "",
      "   ## Sources",
      "   1. <relative path> — <what it contributed>",
      "   ```",
      "",
      "6. **Create one `customer:profile` issue per recommended",
      "   archetype.** Each issue cites the discovery report, names the",
      "   archetype, and identifies the segment. Apply the discover issue's",
      "   priority as the default; escalate if the scope flags a higher-",
      "   priority archetype.",
      "",
      "7. **Commit and push** the discovery report. Close the discover",
      `   issue ${ISSUE_CLOSE_RULE_REFERENCE}.`,
      "",
      "---",
      "",
      "## Phase 2: Profile (`customer:profile`)",
      "",
      "**Goal:** Research one archetype in depth and populate the archetype",
      "page with segment, goals, jobs-to-be-done, constraints, buying",
      "process, technology landscape, and regulatory applicability.",
      "",
      "**Budget:** 4–8 targeted web searches. Writes one archetype page,",
      "creates the blocked competitors issue, and may create",
      "`company:research` and `people:research` issues for representative",
      "customers and notable contacts.",
      "",
      "### Steps",
      "",
      "1. **Read the discovery report** for context on this archetype's",
      "   priority and scope.",
      "",
      "2. **Read the customer-profile-page template** at",
      "   `.claude/skills/profile-customer/_references/templates/_template-customer-profile.md`.",
      "   The template is authoritative — copy its structure verbatim into",
      "   the archetype page, then populate each section from research.",
      "",
      "3. **Read existing archetype pages** in the same `<ARCHETYPE_DIR>`",
      "   to follow consistent structure and avoid duplication.",
      "",
      "4. **Read prior art.** Scan meeting transcripts, industry research,",
      "   and company profiles that reference the representative customers",
      "   in this archetype.",
      "",
      "5. **Execute 4–8 targeted web searches** to fill gaps:",
      "   - Representative customer overviews (primary use cases,",
      "     published case studies)",
      "   - Segment-level buying-process research and analyst coverage",
      "   - Published jobs-to-be-done or customer-interview research",
      "   - Pain points characteristic of the segment",
      "   - Regulations that govern this archetype's operations",
      "",
      "   If a search returns low-quality results, try one reformulated",
      "   query before moving on. Never exceed the 8-search cap.",
      "",
      "6. **Populate the archetype page** at `<ARCHETYPE_PAGE>` by copying",
      "   the template verbatim and filling each section. Every factual",
      "   claim carries a source citation.",
      "",
      "7. **Create the `customer:competitors` issue** blocked on this",
      "   profile issue (`Depends on: #<this-issue>`). Its body references",
      "   the archetype page and the feature matrix path.",
      "",
      "8. **Create `company:research` issues** for representative",
      "   customer organizations that do not already have a canonical",
      "   profile or an open `company:research` issue. The",
      "   `company-profile` bundle owns the downstream profile; this",
      "   agent only hands off. Each issue must carry:",
      "",
      "   - `type:company-profile`",
      "   - `company:research`",
      `   - \`priority:${labelsForPhase(issueDefaults, "company:research").priority}\``,
      `   - \`status:${labelsForPhase(issueDefaults, "company:research").status}\``,
      "",
      ...renderIssueTypeAssignmentStep(),
      "",
      "9. **Create `people:research` issues** for notable contacts",
      "   (economic buyers, technical buyers, user champions) surfaced in",
      "   sources. Keep the list small — at most three individuals per",
      "   profile session — and skip anyone already profiled or queued.",
      "   Each issue must carry:",
      "",
      "   - `type:people-profile`",
      "   - `people:research`",
      `   - \`priority:${labelsForPhase(issueDefaults, "people:research").priority}\``,
      `   - \`status:${labelsForPhase(issueDefaults, "people:research").status}\``,
      "",
      ...renderIssueTypeAssignmentStep(),
      "",
      "10. **Verify all handoffs were created** before committing.",
      "",
      "11. **Commit and push** the archetype page. Close the profile issue",
      `    ${ISSUE_CLOSE_RULE_REFERENCE}.`,
      "",
      "---",
      "",
      "## Phase 3: Competitors (`customer:competitors`)",
      "",
      "**Goal:** Map rows from the shared software-profile feature matrix",
      "to the archetype's needs, identify unmet needs, and hand off each",
      "unmet need as a `req:scan` seed to the requirements-analyst bundle.",
      "",
      "**Budget:** 4–8 targeted web searches — only for closing competitor",
      "coverage gaps the feature matrix does not already answer. No",
      "searches if the matrix is comprehensive enough on its own.",
      "",
      "### Steps",
      "",
      "1. **Read the archetype page** with full goals, jobs, constraints,",
      "   and any preliminary feature-to-need rows.",
      "",
      "2. **Read the shared feature matrix** at `<MATRIX_FILE>`. This is",
      "   the canonical competitor feature data produced by the",
      "   `software-profile` bundle's matrix phase. Rows are scored against",
      "   segment weights — use the scores as a signal but treat the",
      "   archetype's explicit jobs and constraints as authoritative.",
      "",
      "3. **Read existing competitor analyses** in `<COMPETITORS_DIR>` to",
      "   follow consistent structure and avoid duplication.",
      "",
      "4. **Execute up to 4–8 targeted web searches** only to fill",
      "   competitor-coverage gaps the feature matrix does not answer:",
      "   - Competitor products in this archetype's segment that are not",
      "     yet in the matrix",
      "   - Recent competitive moves (M&A, product launches, pricing",
      "     shifts) relevant to the archetype's buying process",
      "   - Published customer complaints or unmet-need signals",
      "",
      "   Skip this step if the matrix already covers the segment.",
      "",
      "5. **Write the competitor-analysis page** at `<COMPETITORS_PAGE>`:",
      "",
      "   ```markdown",
      "   ---",
      '   title: "Competitors: <Archetype Name>"',
      '   description: "Competitive analysis for the <Archetype Name> archetype."',
      "   archetype: <ARCHETYPE_SLUG>",
      "   ---",
      "",
      "   ## Market Overview",
      "   <Brief overview of the competitive landscape this archetype",
      "   evaluates.>",
      "",
      "   ## Key Competitors",
      "",
      "   | Competitor | Relationship | Matrix Row | Threat Level |",
      "   |------------|--------------|------------|--------------|",
      "   | <competitor> | <direct / indirect / adjacent> | <matrix link> | <high / medium / low> |",
      "",
      "   ## Feature-to-Need Map",
      "",
      "   Each row maps one archetype need to the competitor features that",
      "   satisfy it. Competitor features come from `<MATRIX_FILE>`. Rows",
      "   flagged `unmet` are handed off as `req:scan` seeds to the",
      "   requirements-analyst bundle.",
      "",
      "   | Need | Source Job / Goal | Priority | Competitors Covering | Our Product | Status |",
      "   |------|-------------------|----------|----------------------|-------------|--------|",
      "   | <need> | <linked job> | <high / medium / low> | <list or 'None'> | <feature or 'Gap'> | <covered / partial / unmet> |",
      "",
      "   ## Unmet Needs",
      "",
      "   | Need | Priority | Why It's Unmet | Handoff Issue |",
      "   |------|----------|----------------|---------------|",
      "   | <need> | <high / medium / low> | <competitor coverage gap + our gap> | #<N> |",
      "",
      "   ## Implications",
      "   <What the feature-to-need map implies for the consuming",
      "   project's roadmap — which unmet needs are highest leverage,",
      "   which are already well-covered by competitors, which are table",
      "   stakes vs. differentiators.>",
      "",
      "   ## Sources",
      "   1. [<Title>](<URL>) — <brief description>",
      "   ```",
      "",
      "6. **Edit the archetype page's Feature-to-Need Map section** so it",
      "   matches the map authored here. The archetype page stays the",
      "   canonical surface for downstream agents; the competitor page is",
      "   the detailed working document.",
      "",
      "7. **Create `req:scan` issues** for each unmet need. Each issue",
      "   hands off to the `requirements-analyst` bundle so its scan phase",
      "   can deduplicate and open `req:write` issues in the appropriate",
      "   category. Each handoff issue must carry:",
      "",
      "   - `type:requirement`",
      "   - `req:scan`",
      `   - \`priority:${labelsForPhase(issueDefaults, "req:scan").priority}\``,
      `   - \`status:${labelsForPhase(issueDefaults, "req:scan").status}\``,
      "",
      ...renderIssueTypeAssignmentStep(),
      "",
      "   The body must also:",
      "   - Link back to the archetype page and the competitor page",
      "   - Cite the specific unmet-need row and the originating job",
      "   - Escalate the priority above the bundle default when the",
      "     archetype is high-priority and the gap is a deal-breaker",
      "",
      "8. **Create `company:research` issues** for competitor organizations",
      "   referenced in the analysis that do not already have a canonical",
      "   profile or an open `company:research` issue. Each issue must",
      "   carry:",
      "",
      "   - `type:company-profile`",
      "   - `company:research`",
      `   - \`priority:${labelsForPhase(issueDefaults, "company:research").priority}\``,
      `   - \`status:${labelsForPhase(issueDefaults, "company:research").status}\``,
      "",
      ...renderIssueTypeAssignmentStep(),
      "",
      "9. **Create `people:research` issues** for notable competitor",
      "   leadership or customer-advocacy figures surfaced in the analysis.",
      "   Keep this list small — at most three individuals per competitors",
      "   session — and skip anyone already profiled or queued. Each",
      "   issue must carry:",
      "",
      "   - `type:people-profile`",
      "   - `people:research`",
      `   - \`priority:${labelsForPhase(issueDefaults, "people:research").priority}\``,
      `   - \`status:${labelsForPhase(issueDefaults, "people:research").status}\``,
      "",
      ...renderIssueTypeAssignmentStep(),
      "",
      "10. **Commit and push** the competitor-analysis page and the",
      `    updated archetype page. Close the competitors issue ${ISSUE_CLOSE_RULE_REFERENCE}.`,
      "",
      "---",
      "",
      "## Coordination with Other Agents",
      "",
      "### Upstream: meeting-analyst, industry-discovery, research-pipeline",
      "",
      "Meeting transcripts (especially external meetings), industry-",
      "discovery deliverables, and research-pipeline notes are the primary",
      "inputs to the discover phase. A human opens a `customer:discover`",
      "issue when enough upstream signal has accumulated. This agent never",
      "auto-creates `customer:discover` issues.",
      "",
      "### Upstream: software-profile bundle",
      "",
      "The software-profile bundle's matrix phase produces the canonical",
      "feature-matrix file (`<MATRIX_FILE>`) that the competitors phase",
      "consumes. This agent **reads** the matrix; it never writes to it.",
      "If a competitor product is missing from the matrix, this agent",
      "creates a `software:research` issue rather than adding rows itself.",
      "",
      "### Downstream: requirements-analyst bundle",
      "",
      "Phase 3 (Competitors) hands off actionable unmet needs to the",
      "`requirements-analyst` bundle via `req:scan` issues. Each handoff",
      "issue body identifies the originating archetype and job so the",
      "downstream `requirements-writer` phase picks up the right category",
      "template. This agent never writes requirement documents itself.",
      "",
      "### Downstream: company-profile bundle",
      "",
      "This agent creates `company:research` issues for representative",
      "customer organizations (Phase 2) and competitor organizations",
      "(Phase 3) that do not already have a canonical profile. The",
      "`company-profile` bundle's Phase 1 (research) consumes those issues",
      "and produces the canonical profile. **This boundary is load-",
      "bearing:** the customer-profile pipeline targets archetypes;",
      "`company-profile` targets individual companies. Do not profile a",
      "named customer organization in depth inside an archetype page —",
      "hand it off.",
      "",
      "### Downstream: people-profile bundle",
      "",
      "This agent creates `people:research` issues for notable contacts",
      "(buyers, champions, competitor leadership) surfaced during profile",
      "and competitors phases. The `people-profile` bundle's Phase 1",
      "(research) consumes those issues and produces the canonical person",
      "profile. This agent never writes canonical people profiles itself.",
      "",
      "### File Boundaries",
      "",
      "- **Reads:**",
      "  - `<CUSTOMER_ROOT>/` — prior discovery reports, archetype pages,",
      "    and competitor analyses",
      "  - `<DISCOVERY_DIR>/` — discovery reports",
      "  - `<MATRIX_FILE>` — shared feature matrix (read-only)",
      "  - `<docsRoot>/` — project context, industry-research,",
      "    meeting-notes, regulatory-research, and existing company / people",
      "    profiles for framing context",
      "- **Writes:**",
      "  - `<DISCOVERY_DIR>/customer-discover-<SCOPE_SLUG>-<YYYY-MM-DD>.md` (Phase 1)",
      "  - `<ARCHETYPE_PAGE>` (Phase 2; Phase 3 updates the Feature-to-Need Map)",
      "  - `<COMPETITORS_PAGE>` (Phase 3)",
      "- **Never writes to:**",
      "  - Any canonical company-profile or people-profile path (the",
      "    `company-profile` and `people-profile` bundles own those)",
      "  - `<MATRIX_FILE>` (the `software-profile` bundle owns it)",
      "  - `<BCM_DOC_ROOT>/` (the `bcm-writer` bundle owns this)",
      "  - `<REQUIREMENTS_ROOT>/` (the `requirements-writer` bundle owns",
      "    this)",
      "  - `<BUSINESS_MODELS_ROOT>/` (the `business-models` bundle owns",
      "    this)",
      "  - `<REGULATORY_ROOT>/` (the `regulatory-research` bundle owns",
      "    this)",
      "  - `<STANDARDS_ROOT>/` (the `standards-research` bundle owns this)",
      "",
      "---",
      "",
      "## Blocked Issues",
      "",
      "Follow your project's shared Blocked Issues procedure. Additional",
      "block reasons specific to customer research:",
      "",
      "- **No archetype signal.** Discovery phase cannot find enough",
      "  evidence for any archetype to be worth profiling.",
      "- **Feature matrix missing.** Competitors phase requires",
      "  `<MATRIX_FILE>` to exist. If it does not, block and hand off to",
      "  `software-profile` via a `software:matrix` issue.",
      "- **Representative customer behind a non-disclosure.** The archetype",
      "  depends on a customer whose engagement is covered by NDA and",
      "  cannot be summarized from public sources. Block until legal review.",
      "- **Competitors issue arrived before profile completed.** Phase 3",
      "  blocks on the Phase 2 profile issue closing.",
      "- **Regulatory applicability unresolvable.** The archetype",
      "  references regulations that have no regulatory-research page and",
      "  no regulatory:scan issue is open for the scope. Open one and",
      "  block.",
      "",
      "---",
      "",
      "## Rules",
      "",
      "- **One phase per session.** Never run two phases back-to-back in a",
      "  single session.",
      "- **One archetype per profile session.** Phase 2 is scoped to",
      "  exactly one archetype — never profile multiple archetypes at",
      "  once.",
      "- **Persist before closing.** Every phase must write its output file",
      "  before closing its issue.",
      "- **Search budgets are caps, not targets.** Do not exceed 0",
      "  searches in discover, 8 in profile, or 8 in competitors.",
      "- **Customer-centric framing.** Every profile is written from the",
      "  customer's point of view. Do not default to describing the",
      "  consuming project's offering.",
      "- **Archetype, not instance.** Do not profile a named customer",
      "  organization in depth inside an archetype page — hand it off to",
      "  the `company-profile` bundle via `company:research`.",
      "- **Cite everything.** Every non-trivial factual claim carries a",
      "  source citation. Claims about buying process, budget, or decision",
      "  authority require at least Tier 2 support.",
      "- **No fabrication.** Every claim in a discovery report, archetype",
      "  page, or competitor analysis must trace to a cited source. Do not",
      "  populate from general knowledge alone.",
      "- **Relative links only.** Cross-references between docs use",
      "  relative markdown paths, never absolute `https://github.com/...`",
      "  URLs.",
      "- **Read-only over the feature matrix.** Never edit `<MATRIX_FILE>`.",
      "  Missing rows are a `software-profile` concern; open a",
      "  `software:research` or `software:matrix` issue instead.",
      "- **Never cross agent boundaries.** Do not write canonical company",
      "  or people profiles, requirement documents, capability models,",
      "  business-model canvases, regulatory pages, or standards pages.",
      "  Hand off surfaced work via `company:research`, `people:research`,",
      "  `software:research`, `regulatory:scan`, or `req:scan` issues.",
      "- **Unmet needs produce `req:scan` seeds.** Phase 3 is not complete",
      "  until every unmet need in the Feature-to-Need Map has a matching",
      "  `req:scan` issue open or explicitly deferred with a reason.",
    ].join("\n"),
  };
}

/** Skill that kicks off the discover phase of a customer-research campaign. */
function buildDiscoverCustomersSkill(
  paths: ResolvedAgentPaths,
  issueDefaults: ResolvedIssueDefaults,
): AgentSkill {
  return {
    name: "discover-customers",
    description:
      "Kick off the discover phase of a customer-research campaign. Creates a customer:discover issue for the supplied scope (industry, segment, or the consuming project as a whole) and dispatches Phase 1 of the customer-profile-analyst, which scans existing documentation for archetype candidates, writes a discovery report, and creates downstream customer:profile issues.",
    disableModelInvocation: true,
    userInvocable: true,
    context: "fork",
    agent: "customer-profile-analyst",
    platforms: { cursor: { exclude: true } },
    instructions: [
      "# Discover Customer Archetypes",
      "",
      "Kick off a customer-research campaign for a given scope — an",
      "industry, a market segment, or the consuming project as a whole.",
      "Creates a `customer:discover` issue carrying the scope identifier",
      "and dispatches Phase 1 (Discover) in the customer-profile-analyst",
      "agent.",
      "",
      "## Usage",
      "",
      "/discover-customers <scope-slug>",
      "",
      "Optional extensions in the issue body:",
      "- `scope_name: <human-readable name>` — defaults to the slug",
      "  prettified",
      "- `include_archetypes: <list>` — archetypes that must be covered",
      "- `exclude_archetypes: <list>` — archetypes to skip",
      "- `sources: <list>` — additional authorized input paths",
      "",
      "## Default Paths",
      "",
      "If the project has no override in",
      `\`${paths.docsRoot}/project-context.md\` or \`agentConfig.rules\`,`,
      "outputs land under:",
      "",
      `- Discovery report: \`${paths.researchRoot}/customer/customer-discover-<scope-slug>-<YYYY-MM-DD>.md\``,
      `- Archetype pages (Phase 2): \`${paths.docsRoot}/customer-research/archetypes/<archetype-slug>.md\``,
      `- Competitor analyses (Phase 3): \`${paths.docsRoot}/customer-research/competitors/<archetype-slug>.md\``,
      "",
      "## Steps",
      "",
      "1. Create a `customer:discover` issue with `type:customer-profile`,",
      `   \`priority:${labelsForPhase(issueDefaults, "customer:discover").priority}\`, and \`status:${labelsForPhase(issueDefaults, "customer:discover").status}\`. The body must include the`,
      "   scope slug and any known archetypes to include or exclude.",
      "",
      ...renderIssueTypeAssignmentStep(),
      "2. Execute Phase 1 (Discover) of the customer-profile-analyst agent.",
      "3. Phase 1 writes the discovery report and creates one",
      "   `customer:profile` issue per prioritized archetype.",
      "",
      "## Output",
      "",
      "- A discovery report under the project's research directory,",
      "  listing candidate archetypes with segment, size, representative",
      "  customers, and priority.",
      "- One `customer:profile` issue per prioritized archetype.",
    ].join("\n"),
  };
}

/** Skill that kicks off the profile phase for one customer archetype. */
function buildProfileCustomerSkill(
  paths: ResolvedAgentPaths,
  issueDefaults: ResolvedIssueDefaults,
): AgentSkill {
  return {
    name: "profile-customer",
    description:
      "Kick off the profile phase for one customer archetype. Creates a customer:profile issue for the supplied archetype and dispatches Phase 2 of the customer-profile-analyst, which populates the archetype page with segment, goals, jobs-to-be-done, constraints, buying process, technology landscape, and regulatory applicability using the ship-with customer-profile-page template.",
    disableModelInvocation: true,
    userInvocable: true,
    context: "fork",
    agent: "customer-profile-analyst",
    platforms: { cursor: { exclude: true } },
    referenceFiles: CUSTOMER_PROFILE_REFERENCE_FILES,
    instructions: [
      "# Profile Customer Archetype",
      "",
      "Author an archetype profile page for one customer archetype.",
      "Creates a `customer:profile` issue and dispatches Phase 2 (Profile)",
      "in the customer-profile-analyst agent.",
      "",
      "Most campaigns do not invoke this skill directly — Phase 1",
      "(Discover) creates profile issues automatically. Use this skill",
      "when:",
      "",
      "- A single archetype needs refreshed research without a full",
      "  discovery pass",
      "- A new archetype emerges between scheduled discovery sessions",
      "- The discover phase deferred an archetype and the blocker is now",
      "  resolved",
      "- Another agent (e.g. `industry-discovery`) flagged a segment that",
      "  needs its own archetype page",
      "",
      "## Usage",
      "",
      "/profile-customer <archetype-slug>",
      "",
      "Optional extensions in the issue body:",
      "- `discovery_report: <path>` — prior discovery-phase report",
      "  (optional when invoked manually)",
      "- `segment: <slug>` — segment the archetype belongs to",
      "- `sources: <list>` — additional authorized input paths (meeting",
      "  transcripts, industry-research pages, prior customer notes)",
      "",
      "## Customer-Profile Template",
      "",
      "This skill ships a verbatim customer-profile-page template at",
      "`.claude/skills/profile-customer/_references/templates/_template-customer-profile.md`.",
      "The Phase 2 handler reads that file as its authoritative structure —",
      "every archetype page copies the template and fills segment, goals,",
      "jobs-to-be-done, constraints, buying process, technology landscape,",
      "regulatory applicability, and the preliminary feature-to-need map.",
      "",
      "## Default Paths",
      "",
      "If the project has no override in",
      `\`${paths.docsRoot}/project-context.md\` or \`agentConfig.rules\`,`,
      "the archetype page lands at:",
      "",
      `\`${paths.docsRoot}/customer-research/archetypes/<archetype-slug>.md\``,
      "",
      "## Steps",
      "",
      "1. Create a `customer:profile` issue with `type:customer-profile`,",
      `   \`priority:${labelsForPhase(issueDefaults, "customer:profile").priority}\`, and \`status:${labelsForPhase(issueDefaults, "customer:profile").status}\`. The body must include the`,
      "   archetype slug and (optionally) the segment slug.",
      "",
      ...renderIssueTypeAssignmentStep(),
      "2. Execute Phase 2 (Profile) of the customer-profile-analyst agent.",
      "   The agent runs 4–8 targeted web searches and populates the",
      "   archetype page.",
      "",
      "## Output",
      "",
      "- A populated archetype page with segment, goals, jobs-to-be-done,",
      "  constraints, buying process, technology landscape, regulatory",
      "  applicability, and a preliminary feature-to-need map.",
      "- One blocked `customer:competitors` issue for the downstream",
      "  Phase 3.",
      "- `company:research` issues for representative customer",
      "  organizations not yet profiled.",
      "- `people:research` issues for notable contacts (economic buyer,",
      "  technical buyer, user champion) not yet profiled.",
    ].join("\n"),
  };
}

/** Skill that kicks off the competitors phase for one customer archetype. */
function buildAnalyzeCustomerCompetitorsSkill(
  paths: ResolvedAgentPaths,
  issueDefaults: ResolvedIssueDefaults,
): AgentSkill {
  return {
    name: "analyze-customer-competitors",
    description:
      "Kick off the competitors phase for one customer archetype. Creates a customer:competitors issue for the supplied archetype and dispatches Phase 3 of the customer-profile-analyst, which maps rows from the shared software-profile feature matrix to the archetype's needs, identifies unmet needs, and hands off each unmet need as a req:scan seed to the requirements-analyst bundle.",
    disableModelInvocation: true,
    userInvocable: true,
    context: "fork",
    agent: "customer-profile-analyst",
    platforms: { cursor: { exclude: true } },
    instructions: [
      "# Analyze Customer Competitors",
      "",
      "Map rows from the shared software-profile feature matrix to one",
      "customer archetype's needs, identify unmet needs, and hand off each",
      "unmet need to the requirements pipeline as a `req:scan` seed.",
      "Creates a `customer:competitors` issue and dispatches Phase 3",
      "(Competitors) in the customer-profile-analyst agent.",
      "",
      "Most campaigns do not invoke this skill directly — Phase 2",
      "(Profile) creates the competitors issue automatically, blocked on",
      "the profile issue that spawned it. Use this skill when:",
      "",
      "- An archetype page was refreshed and the feature-to-need map",
      "  needs a re-run",
      "- The feature matrix was refreshed with new competitor rows and",
      "  the archetype's map needs to incorporate them",
      "- A human authored the archetype page and needs the competitive",
      "  analysis synthesized separately",
      "- The competitors phase was deferred and the profile is now",
      "  landed",
      "",
      "## Usage",
      "",
      "/analyze-customer-competitors <archetype-slug>",
      "",
      "Required extensions in the issue body:",
      "- `archetype_page: <path>` — link to the archetype profile page",
      "  being analyzed",
      "",
      "Optional extensions:",
      "- `matrix_file: <path>` — override for the shared feature-matrix",
      `  file. Defaults to \`${paths.softwareRoot}/feature-matrix.md\`.`,
      "",
      "## Default Paths",
      "",
      "If the project has no override in",
      `\`${paths.docsRoot}/project-context.md\` or \`agentConfig.rules\`,`,
      "the competitor-analysis page lands at:",
      "",
      `\`${paths.docsRoot}/customer-research/competitors/<archetype-slug>.md\``,
      "",
      "## Steps",
      "",
      "1. Create a `customer:competitors` issue with",
      `   \`type:customer-profile\`, \`priority:${labelsForPhase(issueDefaults, "customer:competitors").priority}\`, and`,
      `   \`status:${labelsForPhase(issueDefaults, "customer:competitors").status}\` (or \`status:blocked\` when still dependent on a`,
      "   Phase 2 profile issue). Body must include the archetype page",
      "   path.",
      "",
      ...renderIssueTypeAssignmentStep(),
      "2. Execute Phase 3 (Competitors) of the customer-profile-analyst",
      "   agent. The agent reads `<MATRIX_FILE>` and runs up to 4–8 web",
      "   searches only to close competitor-coverage gaps the matrix does",
      "   not answer.",
      "",
      "## Output",
      "",
      "- A competitor-analysis page with market overview, key competitors,",
      "  feature-to-need map, unmet-needs table, and implications.",
      "- The archetype page's Feature-to-Need Map section is rewritten to",
      "  match the detailed analysis.",
      "- One `req:scan` issue (consumed by the `requirements-analyst`",
      "  bundle) per unmet need identified. Each handoff carries the",
      "  originating archetype, job, and priority so the downstream",
      "  `requirements-writer` phase picks up the right template.",
      "- `company:research` issues for competitor organizations not yet",
      "  profiled.",
      "- Optional `people:research` issues for notable competitor",
      "  leadership.",
    ].join("\n"),
  };
}

/**
 * Customer-profile bundle — enabled by default.
 *
 * Consuming projects can disable it with
 * `excludeBundles: ["customer-profile"]`. `appliesWhen` always
 * returns `true` per the workflow-bundle peer-present assumption.
 *
 * Ships a sub-agent (`customer-profile-analyst`), three
 * user-invocable skills (`/discover-customers`, `/profile-customer`,
 * `/analyze-customer-competitors`), a customer-profile-page template
 * (emitted alongside the profile skill), and `type:customer-profile`
 * plus `customer:*` phase labels.
 *
 * The bundle sits downstream of `meeting-analysis`,
 * `industry-discovery`, and `research-pipeline` (which surface the
 * need for customer-archetype research) and hands off unmet needs to
 * the `requirements-analyst` bundle via `req:scan` issues, and
 * canonical profiles to `company-profile` and `people-profile` for
 * representative customer organizations, competitor organizations,
 * and notable contacts.
 *
 * Distinct from `company-profile`: the `company-profile` bundle
 * targets any company entity (competitor, vendor, partner, customer
 * organization); this bundle targets **customer archetypes** (the
 * reusable shape of a buyer/user segment) and closes the loop from
 * unmet need to `req:scan` seed via the shared software-profile
 * feature matrix.
 */
export function buildCustomerProfileBundle(
  paths: ResolvedAgentPaths = DEFAULT_AGENT_PATHS,
  issueDefaults: ResolvedIssueDefaults = DEFAULT_RESOLVED_ISSUE_DEFAULTS,
  tier: AgentModel = AGENT_MODEL.BALANCED,
): AgentRuleBundle {
  return {
    name: "customer-profile",
    description:
      "Customer-archetype research pipeline: discover, profile, competitors. 3 phases with customer:* phase labels. Segments customer archetypes, profiles each archetype's goals, jobs-to-be-done, constraints, and buying process, then maps competitor features (from the shared software-profile feature matrix) to each archetype's needs and hands off unmet-need gaps to the requirements-analyst bundle as req:scan seeds. Enabled by default; domain-neutral; customer-centric (distinct from company-profile); filesystem-durable between phases.",
    appliesWhen: () => true,
    rules: [
      {
        name: "customer-profile-workflow",
        description:
          "Describes the 3-phase customer-profile pipeline, the customer:* label taxonomy, and the handoff boundaries with requirements-analyst (req:scan seeds), company-profile, people-profile, and software-profile (read-only feature matrix).",
        scope: AGENT_RULE_SCOPE.ALWAYS,
        content: [
          "# Customer Profile Workflow",
          "",
          "Use `/discover-customers <scope-slug>` to kick off a",
          "customer-archetype research campaign. The pipeline runs in 3",
          "phases — discover, profile, competitors — each tracked by its",
          "own GitHub issue labeled `customer:discover`,",
          "`customer:profile`, or `customer:competitors`. All issues carry",
          "`type:customer-profile`.",
          "",
          "Two additional user-invocable skills drive the downstream",
          "phases directly when the default pipeline needs to be entered",
          "mid-stream:",
          "",
          "- `/profile-customer <archetype-slug>` — author one archetype",
          "  page (normally created by the discover phase automatically).",
          "- `/analyze-customer-competitors <archetype-slug>` — map the",
          "  shared feature matrix to an archetype's needs and hand off",
          "  `req:scan` seeds to the `requirements-analyst` bundle",
          "  (normally created by the profile phase automatically).",
          "",
          "The bundle produces **discovery reports, archetype profile",
          "pages, and competitor-analysis pages with feature-to-need",
          "maps**. It never writes canonical company or people profiles,",
          "requirement documents, capability models, business-model",
          "canvases, regulatory pages, or standards pages — surfaced work",
          "is delegated to the `company-profile`, `people-profile`, and",
          "`requirements-analyst` bundles via `company:research`,",
          "`people:research`, and `req:scan` issues respectively.",
          "",
          "This pipeline is **distinct from `company-profile`**. The",
          "`company-profile` bundle targets any company entity; this",
          "bundle targets **customer archetypes** (the reusable shape of",
          "a buyer/user segment) and maps competitor features to archetype",
          "needs. When an archetype references a specific customer",
          "organization, the pipeline hands off to `company-profile` via",
          "`company:research` rather than duplicating the organizational",
          "profile.",
          "",
          "The competitors phase is **read-only** over the shared",
          "software-profile feature-matrix file. Missing competitor rows",
          "are a `software-profile` concern; this bundle opens",
          "`software:research` issues rather than editing the matrix.",
          "",
          "Consumer-specific vocabularies (which industries, archetypes,",
          "and segments to cover) are supplied per-project via",
          `\`${paths.docsRoot}/project-context.md\` or the invoking`,
          "issue body. The bundle is domain-neutral — no archetypes,",
          "segments, or competitors are hardcoded.",
          "",
          "See the `customer-profile-analyst` agent definition for full",
          "workflow details, default paths, configurable placeholders, and",
          "phase-by-phase instructions.",
        ].join("\n"),
        platforms: {
          cursor: { exclude: true },
        },
        tags: ["workflow"],
      },
    ],
    skills: [
      buildDiscoverCustomersSkill(paths, issueDefaults),
      buildProfileCustomerSkill(paths, issueDefaults),
      buildAnalyzeCustomerCompetitorsSkill(paths, issueDefaults),
    ],
    subAgents: [
      buildCustomerProfileAnalystSubAgent(paths, issueDefaults, tier),
    ],
    labels: [
      {
        name: "type:customer-profile",
        color: "5319E7",
        description:
          "Researches a customer archetype: discovery, deep profile, or competitor analysis",
      },
      {
        name: "customer:discover",
        color: "C5DEF5",
        description:
          "Phase 1: scan docs for archetype candidates, write discovery report, create profile issues",
      },
      {
        name: "customer:profile",
        color: "BFDADC",
        description:
          "Phase 2: deep-research one archetype: segment, goals, JTBD, constraints, buying process, tech",
      },
      {
        name: "customer:competitors",
        color: "D4C5F9",
        description:
          "Phase 3: map feature-matrix rows to archetype needs and hand off req:scan seeds",
      },
    ],
  };
}

/**
 * Default-paths instance of the customer-profile bundle, preserved
 * for backward compatibility with consumers that import the const
 * directly. The factory above is the canonical entry point when a
 * consumer supplies `AgentConfigOptions.paths`.
 */
export const customerProfileBundle: AgentRuleBundle =
  buildCustomerProfileBundle();
